<script setup lang="ts">
import TooltipClick from '@components/TooltipClick.vue';
import { Direction } from '@enums/direction.ts';
import SubmenuItem from '@layout/nav-v2/SubmenuItem.vue';
import { URL_LOGOUT, URL_OFFER_CREATE, URL_USER_NOTIFICATIONS, URL_USER_PROFILE_MANAGE } from '@tools/routes/routes';
import Typography from '@components/Typography.vue';
import { trans } from '@elements/translation.ts';
import Icon from '@components/Icon.vue';
import IconUserCircle from '@icons/ph_user-circle.svg';
import IconBellRinging from '@icons/ph_bell-ringing.svg';
import IconPlus from '@icons/ph_plus.svg';
import IconSignout from '@icons/ph_signout.svg';
import IconShuffle from '@icons/ph_shuffle.svg';
import type { User } from '@models/groups/app';
import UserPicture from '@components/UserPicture.vue';
import Divider from '@components/Divider.vue';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';
import { AccountProfile } from '@enums/accountProfile.ts';
import { storeToRefs } from 'pinia';
import Tooltip from '@components/Tooltip.vue';
import MenuList from '@layout/nav-v2/desktop/components/MenuList.vue';

defineProps<{
  isExpanded: boolean,
  user: User,
}>();

const userProfileStore = useUserProfileStore();
const { switchCurrentProfile } = userProfileStore;
const { userProfile, currentProfile } = storeToRefs(useUserProfileStore());
</script>

<template>
  <component
    :is="isExpanded ? TooltipClick : Tooltip"
    :direction="Direction.RIGHT"
    trigger-class="grow w-full"
    panel-class="w-[288px] !rounded-lg !top-1/2 -translate-y-full ml-4 !p-2"
  >
    <template #trigger>
      <button
        :class="[
          'h-12 w-full',
          'px-3 py-0',
          'flex items-center space-x-2',
          'overflow-hidden',
          { 'w-14 border-0 justify-center hover:bg-transparent': !isExpanded },
        ]"
        data-cy-ref="desktop-user-menu--trigger"
      >
        <UserPicture
          class="flex-shrink-0"
          :user="user"
          :size="32"
        />

        <span
          v-if="isExpanded"
          class="flex items-center space-x-1"
        >
          <Typography
            variant="body"
            class="whitespace-nowrap"
          >
            {{ user.firstname }}
          </Typography>

          <Typography
            v-if="user.organization?.name"
            variant="body-small"
            color="gray-darker"
            class="whitespace-nowrap"
          >
            ({{ user.organization.name }})
          </Typography>
        </span>
      </button>
    </template>

    <template #panel>
      <MenuList data-cy-ref="desktop-user-menu--panel">
        <li>
          <SubmenuItem :link="URL_USER_PROFILE_MANAGE">
            <div class="desktop-user-menu__submenu">
              <Icon
                :svg="IconUserCircle"
                height="24"
                width="24"
                fill="gray-darker"
              />

              <Typography variant="body">
                {{ trans('user_menu.profile') }}
              </Typography>
            </div>
          </SubmenuItem>
        </li>

        <li>
          <SubmenuItem :link="URL_USER_NOTIFICATIONS">
            <div class="desktop-user-menu__submenu">
              <Icon
                :svg="IconBellRinging"
                height="24"
                width="24"
                fill="gray-darker"
              />

              <Typography variant="body">
                {{ trans('user_menu.notifications') }}
              </Typography>
            </div>
          </SubmenuItem>
        </li>

        <template v-if="userProfile !== AccountProfile.SELLER">
          <li>
            <Divider class="m-0" color="gray" />
          </li>

          <li>
            <SubmenuItem
              v-if="userProfile === AccountProfile.BOTH"
              @click="switchCurrentProfile"
            >
              <div class="desktop-user-menu__submenu">
                <Icon
                  :svg="IconShuffle"
                  height="24"
                  width="24"
                  fill="gray-darker"
                />

                <Typography variant="body">
                  {{
                    currentProfile === AccountProfile.SELLER
                      ? trans('user_menu.switch_button.seller')
                      : trans('user_menu.switch_button.buyer')
                  }}
                </Typography>
              </div>
            </SubmenuItem>

            <SubmenuItem
              v-if="userProfile === AccountProfile.BUYER || userProfile === AccountProfile.UNQUALIFIED"
              :link="URL_OFFER_CREATE"
            >
              <div class="desktop-user-menu__submenu">
                <Icon
                  :svg="IconPlus"
                  height="24"
                  width="24"
                  fill="gray-darker"
                />

                <Typography variant="body">
                  {{ trans('user_menu.main.create_offer') }}
                </Typography>
              </div>
            </SubmenuItem>
          </li>
        </template>

        <li>
          <Divider class="m-0" color="gray" />
        </li>

        <li>
          <SubmenuItem :link="URL_LOGOUT">
            <div class="desktop-user-menu__submenu">
              <Icon
                :svg="IconSignout"
                height="24"
                width="24"
                fill="red"
              />

              <Typography
                variant="body"
                color="red"
              >
                {{ trans('global.disconnect') }}
              </Typography>
            </div>
          </SubmenuItem>
        </li>
      </MenuList>
    </template>
  </component>
</template>

<style scoped lang="scss">
.desktop-user-menu {
  &__submenu {
    @apply flex space-x-2;
  }
}
</style>
